import {useSearchParams} from "react-router-dom"
import {Dropdown} from "primereact/dropdown"
import {Skeleton} from "primereact/skeleton"
import {useEffect, useMemo, useState} from "react"
import useSWR from "swr"

import {EducatorOrientationSummary} from "../components/educator/EducatorOrientationSummary"
import {EducatorOrientationArchiveSummary} from "../components/educator/EducatorOrientationArchiveSummary"
import {wrappedGet} from "../utilities/AxiosHelper"
import FilterMultiSelect from "../components/FilterMultiSelect"
import SubHeader from "../components/SubHeader"
import {fetcherGet, scoreBadge} from "../utilities/general"
import {getToken, getPermissions} from "../utilities/useToken"

const SORT_OPTIONS = [
    {label: "Alphabetically (A-Z)", value: "name"},
    {label: "Orientation Start Date (Most Recent-Oldest)", value: "start_date"},
]

const ratingValue = (orientation) => {
    const rating = Number(orientation.current_readiness_rating)
    return Number.isFinite(rating) ? rating : Number.MAX_SAFE_INTEGER
}

const timestamp = (value) => {
    const parsed = Date.parse(value)
    return Number.isFinite(parsed) ? parsed : 0
}

const toOptions = (values) =>
    [...new Set(values)]
        .filter(Boolean)
        .sort()
        .map((v) => ({label: v, value: v}))

// Adds the fields the filters and cards need. Runs once per row at fetch time.
const decorate = (orientation) => {
    const badge = scoreBadge(orientation.current_readiness_rating, 100)
    const weekNumber = parseInt(orientation.current_week_of_hire, 10)
    const rawWeek = String(orientation.current_week_of_hire ?? "").trim()
    // Group 1/1* under 1; keep non-numeric values (e.g. Paused) as-is; blank/future -> N/A.
    const weekKey = Number.isNaN(weekNumber) ? rawWeek || "N/A" : weekNumber
    return {
        ...orientation,
        readiness_badge: badge,
        readiness_label: badge.badge || "No Info",
        experience_label: orientation.experience_level_name || "Not Specified",
        week_key: weekKey,
    }
}

/**
 * One pass over the rows produces the filtered list AND the cascading options.
 *
 * For each row we find which filters it fails:
 *   - fails none:   it is in the filtered list and counts toward every dropdown
 *   - fails one:    it counts only toward that dropdown (a dropdown ignores its own filter)
 *   - fails two+:   it counts toward nothing
 * The search box has no dropdown, so a row that fails only the search counts toward nothing.
 */
const deriveView = (rows, f) => {
    const search = f.searchText.trim().toLowerCase()
    const checks = {
        search: (o) => !search || (o.name || "").toLowerCase().includes(search),
        status: (o) => f.status.length === 0 || f.status.includes(o.readiness_label),
        week: (o) => f.week.length === 0 || f.week.includes(o.week_key),
        unit: (o) => f.unit.length === 0 || f.unit.includes(o.unit_name),
        facility: (o) => f.facility.length === 0 || f.facility.includes(o.facility_name),
        organization: (o) => f.organization.length === 0 || f.organization.includes(o.organization_name),
    }
    const checkKeys = Object.keys(checks)

    const filtered = []
    const statusRanks = new Map()
    const weeks = new Set()
    const units = new Set()
    const facilities = new Set()
    const organizations = new Set()

    const collect = (o, key) => {
        switch (key) {
            case "status": {
                if (!o.readiness_badge.badge) return
                const rank = ratingValue(o)
                const current = statusRanks.get(o.readiness_label)
                if (current === undefined || rank < current) statusRanks.set(o.readiness_label, rank)
                return
            }
            case "week":
                weeks.add(o.week_key)
                return
            case "unit":
                units.add(o.unit_name)
                return
            case "facility":
                facilities.add(o.facility_name)
                return
            case "organization":
                organizations.add(o.organization_name)
                return
            default:
        }
    }

    const dropdownKeys = ["status", "week", "unit", "facility", "organization"]

    rows.forEach((o) => {
        const failed = checkKeys.filter((k) => !checks[k](o))
        if (failed.length === 0) {
            filtered.push(o)
            dropdownKeys.forEach((k) => collect(o, k))
        } else if (failed.length === 1) {
            collect(o, failed[0]) // no-op for "search"
        }
    })

    const weekList = [...weeks]
    return {
        filtered,
        options: {
            status: [...statusRanks.keys()].sort((a, b) => statusRanks.get(a) - statusRanks.get(b)),
            week: [
                ...weekList.filter((w) => typeof w === "number").sort((a, b) => a - b),
                ...weekList.filter((w) => typeof w === "string").sort(),
            ].map((w) => ({label: typeof w === "number" ? `Week ${w}` : w, value: w})),
            unit: toOptions(units),
            facility: toOptions(facilities),
            organization: toOptions(organizations),
        },
    }
}

const EducatorOrientations = () => {
    const [params] = useSearchParams()
    const isArchived = params.get("archived") !== null ? params.get("archived") === "true" : undefined

    const {role} = getToken(true) || {}
    const permissions = getPermissions()

    const canFilterFacility = role === "Administrator" || role === "Enterprise Admin"
    const canFilterOrganization = role === "Enterprise Admin"

    useEffect(() => {
        document.title = "My Team"
    }, [])

    // Fetched once per mode and never modified by filtering
    const [orientations, setOrientations] = useState([])
    const [isLoading, setIsLoading] = useState(true)

    // User input
    const [searchText, setSearchText] = useState("")
    const [statusFilter, setStatusFilter] = useState([])
    const [unitFilter, setUnitFilter] = useState([])
    const [weekFilter, setWeekFilter] = useState([])
    const [facilityFilter, setFacilityFilter] = useState([])
    const [organizationFilter, setOrganizationFilter] = useState([])
    const [sortBy, setSortBy] = useState("name")

    const {data: preceptors} = useSWR("/preceptors", fetcherGet, {fallbackData: []})

    useEffect(() => {
        // switched from SWR because we are navigated away from this route by a nested component
        let cancelled = false
        const fetchData = async () => {
            setIsLoading(true)
            const response = await wrappedGet(`/unit-orientations${isArchived ? "?archived=true" : ""}`)
            if (cancelled) return
            setOrientations(response.data.results.map(decorate))
            setIsLoading(false)
        }
        fetchData()
        return () => {
            cancelled = true
        }
    }, [isArchived])

    // filtered list + cascading options in a single pass.
    // Every input is listed individually, so the dependency array is honest.
    const {filtered, options} = useMemo(
        () =>
            deriveView(orientations, {
                searchText,
                status: statusFilter,
                week: weekFilter,
                unit: unitFilter,
                facility: facilityFilter,
                organization: organizationFilter,
            }),
        [orientations, searchText, statusFilter, weekFilter, unitFilter, facilityFilter, organizationFilter]
    )

    // one pass over the already-filtered list
    const visibleOrientations = [...filtered].sort(
        sortBy === "start_date"
            ? (a, b) => timestamp(b.start_date) - timestamp(a.start_date)
            : (a, b) => (a.name || "").localeCompare(b.name || "")
    )

    const hasActiveFilters =
        !!searchText.trim() ||
        statusFilter.length > 0 ||
        unitFilter.length > 0 ||
        weekFilter.length > 0 ||
        facilityFilter.length > 0 ||
        organizationFilter.length > 0

    const readinessCounts = {}
    // Pre-seed all four readiness buckets so every card renders (even with 0)
    ;[20, 50, 70, 90].forEach((percent) => {
        const badge = scoreBadge(percent, 100)
        readinessCounts[badge.badge] = {cnt: 0, badge}
    })
    const archiveCounts = {
        successful: {count: 0, label: "Successful"},
        attritioned: {count: 0, label: "Attritioned"},
        cancelled: {count: 0, label: "Cancelled"},
    }
    let activeStarted = 0
    const now = new Date()

    filtered.forEach((o) => {
        if (o.readiness_badge.badge) {
            const key = o.readiness_label
            if (!readinessCounts[key]) readinessCounts[key] = {cnt: 0, badge: o.readiness_badge}
            readinessCounts[key].cnt++
        }
        if (o.orientation_status === "Completed") archiveCounts.successful.count++
        else if (o.orientation_status === "Attritioned") archiveCounts.attritioned.count++
        else if (o.orientation_status === "Cancelled") archiveCounts.cancelled.count++
        if (o.orientation_status === "Active" && now >= new Date(o.start_date)) activeStarted++
    })

    const sortedCounts = Object.entries(readinessCounts).sort(([, a], [, b]) => a.badge.order - b.badge.order)
    const totalCount = isArchived ? filtered.length : activeStarted

    let title = "My Active Orientees"
    if (isArchived) {
        title = "My Archived Orientees"
    }

    return (
        <>
            <SubHeader title1="" title2={title} icon="fa-user-nurse">
                {!isArchived && permissions.allow_osrs && (
                    <div className="d-flex flex-row flex-grow-1 justify-content-end align-items-center gap-3">
                        <a
                            href={`/surveys/orientation-status-report-selection?guestPreceptor=true`}
                            className="btn btn-light-primary d-flex align-items-sm-center  px-3"
                        >
                            Complete OSR as Guest Preceptor
                        </a>
                    </div>
                )}
            </SubHeader>

            <div className="container d-flex flex-column">
                {isLoading ? (
                    <div className="card p-8 mt-6">
                        <Skeleton width="100%" height="10rem" />
                    </div>
                ) : (
                    <>
                        <div className="card mt-4 border border-gray-400">
                            <div className="card-body py-4 px-4 px-md-8">
                                <div className="row g-8">
                                    <div className="col-lg-3 col-md-6">
                                        <div className="d-flex flex-column">
                                            <label
                                                htmlFor="orientee-search"
                                                className="fs-6 form-label fw-bold text-dark"
                                            >
                                                Search Orientees
                                            </label>
                                            <div className="d-flex align-items-center position-relative">
                                                <i className="fas fa-magnifying-glass fs-3 position-absolute ms-4 text-gray-600"></i>
                                                <input
                                                    id="orientee-search"
                                                    type="text"
                                                    className="form-control form-control-solid fw-bold ps-12"
                                                    placeholder="Enter Name"
                                                    value={searchText}
                                                    onChange={(event) => setSearchText(event.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <FilterMultiSelect
                                        inputId="readinessCategoryId"
                                        label="Readiness Category"
                                        value={statusFilter}
                                        options={options.status}
                                        onChange={setStatusFilter}
                                    />
                                    <FilterMultiSelect
                                        inputId="week"
                                        label="Week Number"
                                        value={weekFilter}
                                        options={options.week}
                                        onChange={setWeekFilter}
                                    />
                                    {canFilterOrganization && (
                                        <FilterMultiSelect
                                            inputId="organization"
                                            label="Organization"
                                            value={organizationFilter}
                                            options={options.organization}
                                            onChange={setOrganizationFilter}
                                        />
                                    )}
                                    {canFilterFacility && (
                                        <FilterMultiSelect
                                            inputId="facility"
                                            label="Facility"
                                            value={facilityFilter}
                                            options={options.facility}
                                            onChange={setFacilityFilter}
                                        />
                                    )}
                                    <FilterMultiSelect
                                        inputId="unit"
                                        label="Unit"
                                        value={unitFilter}
                                        options={options.unit}
                                        onChange={setUnitFilter}
                                    />
                                    <div className="col-lg-3 col-md-6">
                                        <div className="d-flex flex-column">
                                            <label htmlFor="sort-by" className="fs-6 form-label fw-bold text-dark">
                                                Sort By
                                            </label>
                                            <Dropdown
                                                inputId="sort-by"
                                                value={sortBy}
                                                options={SORT_OPTIONS}
                                                onChange={(event) => setSortBy(event.value)}
                                                className="w-100"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {visibleOrientations.length === 0 && (
                            <div className="card mt-4">
                                <div className="card-body text-center fw-semibold text-gray-600 py-10">
                                    {hasActiveFilters ? "No orientees match your filters." : "No active orientees."}
                                </div>
                            </div>
                        )}
                        <div className="d-flex flex-wrap gap-4 d-none d-md-flex mt-4">
                            <div className="border rounded flex-fill py-3 px-4 w-100 w-md-auto bg-white border-gray-400">
                                <div className="d-flex align-items-center">
                                    <div className="fs-2 fw-bold">{totalCount}</div>
                                </div>
                                <div className="fw-semibold fs-6">
                                    {!isArchived && "Total Active"}
                                    {isArchived && "Total Orientees"}
                                </div>
                            </div>
                            {!isArchived &&
                                sortedCounts.map(([label, data]) => (
                                    <div
                                        key={label}
                                        className={`border rounded flex-fill py-3 px-4 w-100 w-md-auto ${data.badge.background} ${data.badge.border} ${data.badge.textColor}`}
                                    >
                                        <div className="d-flex align-items-center">
                                            <div className="fs-2 fw-bold">{data.cnt}</div>
                                        </div>
                                        <div className="fw-semibold fs-6">{label}</div>
                                    </div>
                                ))}

                            {isArchived &&
                                Object.entries(archiveCounts).map(([key, data]) => (
                                    <div
                                        key={key}
                                        className={`border rounded flex-fill py-3 px-4 w-100 w-md-auto bg-white border-gray-400`}
                                    >
                                        <div className="d-flex align-items-center">
                                            <div className="fs-2 fw-bold">{data.count}</div>
                                        </div>
                                        <div className="fw-semibold fs-6">{data.label}</div>
                                    </div>
                                ))}
                        </div>
                        {!isArchived &&
                            visibleOrientations.map((orientation) => (
                                <EducatorOrientationSummary orientation={orientation} key={orientation.id} />
                            ))}
                        {isArchived &&
                            visibleOrientations.map((orientation) => (
                                <EducatorOrientationArchiveSummary
                                    orientation={orientation}
                                    key={orientation.id}
                                    preceptors={preceptors}
                                />
                            ))}
                    </>
                )}
            </div>
        </>
    )
}

export default EducatorOrientations
